import { useState, useRef, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { Lightbulb, BookOpenIcon, Menu, MessageCircle, X } from 'lucide-react'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@components/ui/resizable"

import { useCurrentUser } from '@contexts/currentUser'
import Breadcrumbs from '@components/Breadcrumbs'
import NavTabs from '@components/NavTabs/NavTabs'
import { useBreakpoint } from '@contexts/breakpoints'
import { EducatorProjectSubmissionChannelProvider } from '@contexts/educatorProjectSubmissionChannelContext'
import { useOnboardingChecklist } from '../../students/hooks/onboardingChecklist'
import { formatShortDuration, formatDuration } from '@helpers/format'
import NotFound from '@components/NotFound'

import { useEducatorProjectSubmission } from './hooks'
import ChatPlayground from './ChatPlayground'
import Skeleton from './Skeleton'
import SubmitModal from './SubmitModal'
import ProjectBlocks from './ProjectBlocks'
import LessonOutline from './LessonOutline'
import { Button } from '@components/ui/button'
import { PlainProgressBar } from './ProgressBar'
import { Routes, Route, useNavigate } from 'react-router-dom'
import InstantFeedbackProvider from './InstantFeedbackProvider'

const EducatorProjectSubmission = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const contentPanelRef = useRef(null)
  const isMobile = useBreakpoint('sm')
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0)
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(0)
  const [isChatCollapsed, setIsChatCollapsed] = useState(false)
  const { seriesId, projectId: educatorProjectId } = useParams()

  const { project, submission, isLoading, error } = useEducatorProjectSubmission({ educatorProjectId })
  const { currentMembership: { organization: { id: organizationId, features } } } = useCurrentUser()
  const isCourseInsightsEnabled = features.includes('course_insights')

  const methods = useForm({ mode: 'onTouched', defaultValues: { message: null, attachments: [] } })

  const isNotFound = error?.response?.errors?.some(errorItem => errorItem.extensions?.code === 'NOT_FOUND')
  const isForbidden = error?.response?.errors?.some(errorItem => errorItem.extensions?.code === 'FORBIDDEN' || errorItem.extensions?.code === 'UNAUTHORIZED')

  // Calculate remaining time based on dueDate and server's currentTime
  useEffect(() => {
    if (!project?.series?.assignment?.dueDate || !project?.series?.assignment?.currentTime) return

    const serverTime = new Date(project.series.assignment.currentTime)
    const dueDate = new Date(project.series.assignment.dueDate)
    const initialTimeRemaining = Math.floor((dueDate - serverTime) / 1000)

    // Initialize the time remaining from server time
    setTimeRemainingSeconds(Math.max(0, initialTimeRemaining))

    // If already past due date according to server time, just return
    if (initialTimeRemaining <= 0) {
      return
    }

    const startTime = Date.now()

    // Update time every second
    const timer = setInterval(() => {
      // Calculate elapsed time since component mounted
      const elapsedSinceMount = Math.floor((Date.now() - startTime) / 1000)

      // Calculate remaining time by subtracting elapsed time from initial remaining time
      const remaining = initialTimeRemaining - elapsedSinceMount
      setTimeRemainingSeconds(Math.max(0, remaining))

      // Clear interval if time is up
      if (remaining <= 0) {
        clearInterval(timer)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [project?.series?.assignment?.dueDate, project?.series?.assignment?.currentTime])

  const currentBlock = project?.blocks[currentBlockIndex]
  const currentBlockSubmission = submission?.blockSubmissions?.find(blockSubmission => blockSubmission.blockId === currentBlock?.blockId)
  const projectName = project?.name || 'Untitled lesson'
  const seriesName = project?.series?.name || 'Untitled pathway'
  const assignment = project?.series?.assignment
  const isAllBlocksCompleted = submission?.blockSubmissions?.every(submission => submission.status === 'COMPLETED') && submission?.blockSubmissions?.length === project?.blocks?.length

  const { markOnboardingItemCompleted } = useOnboardingChecklist()
  useEffect(() => markOnboardingItemCompleted('startedSeries'))

  const courseUrl = seriesId ? `/students/courses/${seriesId}` : '/students/courses'

  if (isNotFound) {
    return (
      <NotFound
        title="Lesson not found"
        message="Sorry, we couldn't find the lesson you're looking for. It might have been moved, deleted, or you don't have permission to access it."
        breadcrumbs={<></>}
        backTo={courseUrl}
        backLabel={`Back to pathway`}
      />
    )
  }

  if (isForbidden) {
    return (
      <NotFound
        title="Access denied"
        message="You don't have permission to access this lesson. Please contact your instructor if you believe this is an error."
        breadcrumbs={<></>}
        backTo="/"
        backLabel={`Back to home`}
      />
    )
  }

  if (isLoading || !project || !submission) return <Skeleton />

  const moveToNextBlock = () => {
    const nextIndex = currentBlockIndex + 1
    setCurrentBlockIndex(nextIndex)
  }

  const moveToPreviousBlock = () => {
    setCurrentBlockIndex(currentBlockIndex - 1)

  }

  const setBlockIndex = (index) => {
    setCurrentBlockIndex(index)
    if (isMobile) {
      navigate(`lesson`)
    }
  }

  const toggleChatCollapse = () => {
    setIsChatCollapsed(!isChatCollapsed)
  }

  return (
    <EducatorProjectSubmissionChannelProvider
      submissionId={submission.id}
      organizationId={organizationId}
    >
      <InstantFeedbackProvider project={project} submission={submission}>
        {({ setBlockFeedbackLoading, getBlockFeedbackState }) => (
          <div className='h-full flex flex-col max-h-full'>
            <Breadcrumbs className='overflow-x-auto flex items-center justify-between w-full'>

              <Breadcrumbs.Link label={seriesName} to={`/students/courses/${project.series.id}`} />
              <Breadcrumbs.Text label={projectName} />

              <div className='flex items-center gap-3 sm:flex ml-auto'>
                <If condition={!(submission.status === 'COMPLETED' || submission.status === 'RETURNED') && project?.series?.assignment?.dueDate}>
                  <div className='ml-auto text-center py-1 px-2 bg-accent text-accent-foreground rounded-md'>

                    <Choose>
                      <When condition={timeRemainingSeconds <= 0}>
                        Time's up, submit your work
                      </When>

                      <When condition={timeRemainingSeconds < 120}>
                        <span className='font-semibold'>Time Remaining:</span>&nbsp;{formatDuration(timeRemainingSeconds)}
                      </When>

                      <When condition={timeRemainingSeconds > 60 * 60 * 24}>
                        {/* Display nothing because the submission deadline is more than 24 hours away and therefore should not be on the lesson page */}
                        <span className='font-semibold'>Time Remaining:</span>&nbsp;{formatDuration(timeRemainingSeconds)}
                      </When>

                      <Otherwise>
                        <span className='font-semibold'>Time Remaining:</span>&nbsp;{formatShortDuration(timeRemainingSeconds)}
                      </Otherwise>
                    </Choose>
                  </div>
                </If>

                <div className='flex items-center gap-3 ml-auto'>
                  <If condition={!(submission.status === 'COMPLETED' || submission.status === 'RETURNED')}>
                    <If condition={(project.series.assignment.dueDate && timeRemainingSeconds <= 0) || isAllBlocksCompleted}>
                      <Button
                        id='submit-button'
                        onClick={() => setIsSubmitModalOpen(true)}
                      >
                        Submit Lesson
                      </Button>
                    </If>
                  </If>
                  <If condition={(submission.status === 'COMPLETED' || submission.status === 'RETURNED') && isCourseInsightsEnabled}>
                    <Button
                      variant="link"
                      onClick={() => navigate(`/students/courses/${project.series.id}/lessons/${project.id}/insights`)}
                    >
                      <Lightbulb className='size-5 inline-block mr-1' />
                      Insights
                    </Button>
                  </If>

                  <If condition={submission.status === 'RETURNED' && submission?.score && project?.totalPossibleScore}>
                    <p className='flex text-sm text-gray-500 shrink-0 bg-accent text-accent-foreground rounded-md p-1 border border-border'>
                      {submission?.score} / {project?.totalPossibleScore}
                    </p>
                  </If>
                </div>
              </div>
            </Breadcrumbs>

            <Choose>
              <When condition={isMobile}>
                <div className='h-[calc(100%-64px)] flex flex-col'>

                  <NavTabs className='sticky top-0'>
                    <NavTabs.Link
                      to=''
                      end
                      icon={<Menu size={16} />}
                      label='outline'
                    />
                    <NavTabs.Link
                      to='lesson'
                      end
                      icon={<BookOpenIcon size={16} />}
                      label='Lesson'
                    />
                    {project?.chatEnabled && (
                      <NavTabs.Link
                        to='chat'
                        end
                        icon={<MessageCircle size={16} />}
                        label='Chat'
                      />
                    )}
                  </NavTabs>


                  <div className='h-[calc(100%-40px)] overflow-auto'>
                    <Routes>
                      <Route
                        path=""
                        element={
                          <LessonOutline
                            projectName={projectName}
                            blocks={project.blocks}
                            blockSubmissions={submission.blockSubmissions}
                            currentBlockIndex={currentBlockIndex}
                            setCurrentBlockIndex={setBlockIndex}
                            getBlockFeedbackState={getBlockFeedbackState}
                          />
                        }
                      />
                      <Route
                        path="lesson"
                        element={
                          <div ref={contentPanelRef} className='pb-4 flex flex-col h-full overflow-y-auto'>
                            <ProjectBlocks
                              blocks={project.blocks}
                              blockSubmissions={submission.blockSubmissions}
                              submissionId={submission.id}
                              assignmentId={assignment.id}
                              submissionStatus={submission.status}
                              currentBlock={currentBlock}
                              currentBlockSubmission={currentBlockSubmission}
                              currentBlockIndex={currentBlockIndex}
                              moveToNextBlock={moveToNextBlock}
                              moveToPreviousBlock={moveToPreviousBlock}
                              isSubmissionClosed={project?.series?.assignment?.closed}
                              setIsSubmitModalOpen={setIsSubmitModalOpen}
                              isTimeUp={project?.series?.assignment?.dueDate && timeRemainingSeconds <= 0}
                              feedbackType={project?.feedbackType}
                              setBlockFeedbackLoading={setBlockFeedbackLoading}
                              getBlockFeedbackState={getBlockFeedbackState}
                            />
                            <PlainProgressBar
                              blocks={project.blocks}
                              blockSubmissions={submission.blockSubmissions}
                              className='absolute bottom-0 left-0 w-full'
                            />
                          </div>
                        }
                      />
                      <Route
                        path="chat"
                        element={
                          project?.chatEnabled && (
                            <div className='h-full w-full bg-background'>
                              <FormProvider {...methods}>
                                <ChatPlayground
                                  submissionId={submission.id}
                                  isSubmissionClosed={assignment?.closed}
                                  proFeaturesEnabled={assignment?.proFeaturesEnabled}
                                  submissionStatus={submission.status}
                                />
                              </FormProvider>
                            </div>
                          )
                        }
                      />

                    </Routes>
                  </div>
                </div>
              </When>

              <Otherwise>
                <ResizablePanelGroup
                  className='flex grow bg-background max-h-[calc(100vh-55px)]'
                  style={{ overflow: 'auto' }}
                  direction='horizontal'
                  id='studentProjectPlayground'
                >
                  <ResizablePanel defaultSize={20} minSize={15} maxSize={30} className='flex flex-col bg-background max-h-[calc(100vh-60px)] overflow-y-scroll'>
                    <LessonOutline
                      projectName={projectName}
                      blocks={project.blocks}
                      blockSubmissions={submission.blockSubmissions}
                      currentBlockIndex={currentBlockIndex}
                      setCurrentBlockIndex={setBlockIndex}
                      getBlockFeedbackState={getBlockFeedbackState}
                    />
                  </ResizablePanel>
                  <ResizableHandle withHandle />
                  <ResizablePanel
                    defaultSize={isChatCollapsed || !project?.chatEnabled ? 80 : 50}
                    className='flex flex-col max-h-full'
                  >
                    <div className='h-full' ref={contentPanelRef}>
                      <ProjectBlocks
                        blocks={project.blocks}
                        blockSubmissions={submission.blockSubmissions}
                        submissionId={submission.id}
                        assignmentId={assignment.id}
                        submissionStatus={submission.status}
                        currentBlock={currentBlock}
                        currentBlockSubmission={currentBlockSubmission}
                        currentBlockIndex={currentBlockIndex}
                        moveToNextBlock={moveToNextBlock}
                        moveToPreviousBlock={moveToPreviousBlock}
                        isSubmissionClosed={project?.series?.assignment?.closed}
                        setIsSubmitModalOpen={setIsSubmitModalOpen}
                        isTimeUp={project?.series?.assignment?.dueDate && timeRemainingSeconds <= 0}
                        feedbackType={project?.feedbackType}
                        setBlockFeedbackLoading={setBlockFeedbackLoading}
                        getBlockFeedbackState={getBlockFeedbackState}
                      />
                    </div>

                  </ResizablePanel>

                  <If condition={project?.chatEnabled && !isChatCollapsed}>
                    <ResizableHandle withHandle />
                    <ResizablePanel
                      defaultSize={30}
                      minSize={25}
                      className='relative overflow-auto'
                    >
                      <div className="absolute top-2 right-2 z-20">
                        <button
                          onClick={toggleChatCollapse}
                          className="p-2 rounded-sm bg-background  hover:bg-accent"
                          aria-label="Collapse chat panel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <FormProvider {...methods}>
                        <ChatPlayground
                          submissionId={submission.id}
                          isSubmissionClosed={assignment?.closed}
                          proFeaturesEnabled={assignment?.proFeaturesEnabled}
                          submissionStatus={submission.status}
                        />
                      </FormProvider>
                    </ResizablePanel>
                  </If>

                  <If condition={project?.chatEnabled && isChatCollapsed}>
                    <div className="flex justify-center ml-1">
                      <button
                        onClick={toggleChatCollapse}
                        className="p-2 bg-background border-l border-border shadow-sm hover:bg-accent"
                        aria-label="Expand chat panel"
                      >
                        <MessageCircle size={16} />
                      </button>
                    </div>
                  </If>
                </ResizablePanelGroup>
              </Otherwise>
            </Choose>

            <SubmitModal
              seriesId={project?.series?.id}
              educatorProjectId={educatorProjectId}
              closeModal={() => setIsSubmitModalOpen(false)}
              isOpen={isSubmitModalOpen}
            />
          </div>
        )}
      </InstantFeedbackProvider>
    </EducatorProjectSubmissionChannelProvider>
  )
}

export default EducatorProjectSubmission
